import type { CliApiClient, CliSubmissionResponse } from "../api/cli-api.js";
import { printInfo } from "./output.js";

export class SubmissionWaitInterruptedError extends Error {
  readonly submissionId: string;

  constructor(submissionId: string) {
    super("Submission wait interrupted");
    this.name = "SubmissionWaitInterruptedError";
    this.submissionId = submissionId;
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error("aborted"));
      return;
    }

    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error("aborted"));
    };

    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export async function pollUntilTerminal(
  client: CliApiClient,
  submissionId: string,
  watch: boolean,
): Promise<CliSubmissionResponse> {
  let lastStatus = "";
  const controller = new AbortController();

  const onInterrupt = () => {
    controller.abort();
  };

  process.once("SIGINT", onInterrupt);
  try {
    while (!controller.signal.aborted) {
      const current = await client.getArtifactSubmission(submissionId);
      if (watch && current.status !== lastStatus) {
        printInfo(`Submission ${submissionId}: ${current.status}`);
        lastStatus = current.status;
      }
      if (current.terminal) return current;

      const sleepSec = Math.max(current.next_poll_after_seconds ?? 15, 1);
      try {
        await sleep(sleepSec * 1000, controller.signal);
      } catch {
        throw new SubmissionWaitInterruptedError(submissionId);
      }
    }

    throw new SubmissionWaitInterruptedError(submissionId);
  } finally {
    process.removeListener("SIGINT", onInterrupt);
  }
}
